import { isPathMatches } from '@terminal/components/app-header/utils/navigation-utils';
import {
  ChatInputConfig,
  ChatSubmitEvent,
  SubmitOptions,
} from '@terminal/components/chat';
import { stripMentionMarkers } from '@terminal/components/chat/chat-input/mentions/markers';
import {
  EntityOrThemeWithListTypes,
  isThemeItem,
} from '@terminal/components/search-modal/utils';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  TrackingMetadata,
} from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useDocumentSearch } from '@terminal/views/alfred/document-search/use-document-search/useDocumentSearch';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { useTranslation } from 'react-i18next';

interface UseChatInputHandlersProps {
  config?: ChatInputConfig;
  chatContext?: {
    documents?: string[];
    documentTypes?: string[];
    entityTags?: string[];
  };
  submitCallback?: (message?: string) => void;
  isLoading: boolean;
  trackingMetadata?: TrackingMetadata;
}

export const useChatInputHandlers = ({
  config,
  chatContext,
  submitCallback,
  isLoading,
  trackingMetadata,
}: UseChatInputHandlersProps) => {
  const { t } = useTranslation('chat');
  const { params, deleteQueryParam } = useQueryParams();
  const { navigateToAlfred } = useChatNavigation();
  const { trackAlfredQuerySent } = usePosthogEvents();
  const { navigateToAssetOverview } = useNavigateToAssetOverview();
  const { navigateToThemeOverview } = useNavigateToThemeOverview();
  const {
    resetStore,
    abortRequest,
    resetSession,
    clearDocumentContextMessages,
    sendMessage,
    setIsSearchWithinContext,
  } = useDocumentSearch(state => ({
    resetStore: state.resetStore,
    abortRequest: state.abortRequest,
    resetSession: state.resetSession,
    clearDocumentContextMessages: state.clearDocumentContextMessages,
    sendMessage: state.sendMessage,
    setIsSearchWithinContext: state.setIsSearchWithinContext,
  }));

  const isDocSearchEnabled = useFlags(state =>
    state.getFeatureFlagState('enable_doc_search')
  );

  const shouldResetStore = (question: string) => {
    if (!isPathMatches(['alfred'])) {
      return true;
    }

    const restRegex = new RegExp(`^${t('resetSession')}$`, 'i');
    return restRegex.test(question);
  };

  const closeDocumentSourcesModal = () => {
    if (params[queryKeys.citationGroupId]) {
      deleteQueryParam(queryKeys.citationGroupId);
    }
  };

  const submitHandler = (
    inputValue: string,
    onSubmitCallbackHandler: () => void,
    options?: SubmitOptions
  ) => {
    if (isLoading || !inputValue.trim()) {
      return;
    }

    const question = inputValue.trim();
    const trackingValue = stripMentionMarkers(question);

    if (config?.clearDocumentContextOnSubmit) {
      resetSession(true);
      clearDocumentContextMessages();
    }

    // reset store if asking question not from alfred
    // in other words, init new session
    if (shouldResetStore(trackingValue)) {
      resetStore();
    }

    sendMessage({
      question,
      mentions: options?.mentions,
      ...(chatContext && {
        ancillary: {
          documents: chatContext?.documents,
          document_types: chatContext?.documentTypes,
          entity_tags: chatContext?.entityTags,
        },
      }),
    });

    const messageId = useDocumentSearch.getState().messages.at(-1)?.id;

    if (trackingMetadata?.source) {
      trackAlfredQuerySent({
        source: trackingMetadata.source,
        type: 'Document',
        via: trackingMetadata.via,
        value: trackingValue,
        messageId,
      });
    } else if (isPathMatches(['alfred'])) {
      trackAlfredQuerySent({
        source: POSTHOG_TRACKING_SOURCES.ALFRED,
        type: 'Document',
        value: trackingValue,
        messageId,
      });
    }

    onSubmitCallbackHandler();
    setIsSearchWithinContext(true);
    closeDocumentSourcesModal();
    submitCallback?.(trackingValue);

    if (isDocSearchEnabled) {
      navigateToAlfred('document', {
        trackingSource: trackingMetadata?.source,
      });
    }
  };

  const onSelect = (item: EntityOrThemeWithListTypes, clear: () => void) => {
    const isTheme = isThemeItem(item);
    const source =
      trackingMetadata?.source ?? POSTHOG_TRACKING_SOURCES.DOCUMENT_SEARCH;

    if (isTheme) {
      navigateToThemeOverview(item.id, source);
    } else {
      navigateToAssetOverview(item.tag, source);
    }

    clear();
  };

  const onKeyDown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  const onSubmit = (
    event: ChatSubmitEvent,
    inputText: string,
    onSubmitCallbackHandler: () => void,
    options?: SubmitOptions
  ) => {
    event.preventDefault();
    submitHandler(inputText, onSubmitCallbackHandler, options);
  };

  return {
    onSubmit,
    onKeyDown,
    onSelect,
    onAbort: abortRequest,
  };
};
